import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import { useGeolocated } from "react-geolocated";
import GoogleMapReact from "google-map-react";
import {
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Grid,
  ListItem,
  ListItemText,
} from "@mui/material";
import MDButton from "components/MDButton";

function CurrentGPSLocation({ formRef, draftIndex, displayName, fieldRef, options }) {
  const [fieldError, showFieldError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [gpsLocationCoords, setGpsLocationCoords] = useState(null);
  const [openMapDialog, setOpenMapDialog] = useState(false);

  const { coords, isGeolocationAvailable, isGeolocationEnabled } =
    useGeolocated({
      positionOptions: {
        enableHighAccuracy: true,
        maximumAge: 0,
      },
      // watchPosition: true,
      userDecisionTimeout: 5000,
    });

  function getGPSCoords() {
    if (!isGeolocationAvailable) {
      return false;
    }

    if (!isGeolocationEnabled) {
      return false;
    }

    if (coords) {
      console.log(coords.accuracy);
      return { latitude: coords.latitude, longitude: coords.longitude };
    }

    return false;
  }

  function captureLocation() {
    setGpsLocationCoords(
      JSON.stringify({
        latitude: coords.latitude,
        longitude: coords.longitude,
      }),
    );
  }

  const [hasSavedValue, setHasSavedValue] = useState(false);
  const [draftValue, setDraftValue] = useState(null);

  useEffect(() => {
    if (!formRef) return;

    const saved = localStorage.getItem(`draft_${formRef}`);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      const val = parsed?.[draftIndex].values?.[fieldRef];

      if (val !== undefined && val !== null && val !== "") {
        setDraftValue(JSON.parse(val));
        setHasSavedValue(true);
      }
    } catch (e) {
      console.error("Draft parse error", e);
    }
  }, [formRef, fieldRef]);

  return (
    <MDBox mt={3}>
      {hasSavedValue ? (
        <MDBox>
          <ListItem divider>
            <MDBox mr={1}>
              <Icon color="success">check_circle</Icon>
            </MDBox>
            <ListItemText
              primary={
                <MDTypography variant="h6" fontSize={14} fontWeight="regular">
                  {displayName}
                </MDTypography>
              }
              secondary={
                <MDTypography
                  variant="caption"
                  fontSize={13}
                  fontWeight="light"
                >
                  <b>
                    <Icon color="error">pin_drop</Icon>&nbsp;GPS Captured
                  </b>
                  <br />
                  <small>
                    Latitude: {draftValue.latitude} | Longitude:{" "}
                    {draftValue.longitude}
                  </small>
                </MDTypography>
              }
            />
            <MDButton
              size="small"
              color="warning"
              variant="outlined"
              iconOnly
              onClick={() => {
                try {
                  const key = `draft_${formRef}`;
                  const saved = localStorage.getItem(key);
                  if (!saved) return;

                  const parsed = JSON.parse(saved);

                  if (parsed?.values && fieldRef in parsed.values) {
                    delete parsed.values[fieldRef];
                  }

                  // persist updated draft
                  localStorage.setItem(key, JSON.stringify(parsed));

                  // update UI state
                  setHasSavedValue(false);
                  setDraftValue(null);
                } catch (e) {
                  console.error("Failed to clear draft field", e);
                }
              }}
            >
              <Icon>edit</Icon>
            </MDButton>
          </ListItem>
          <input
            type="hidden"
            id={`dff_${fieldRef}`}
            name={fieldRef}
            value={JSON.stringify(draftValue)}
          />
        </MDBox>
      ) : (
        <MDBox>
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            autoHideDuration={5000}
            onClose={() => showFieldError(false)}
            open={fieldError}
          >
            <Alert severity="error" sx={{ width: "100%" }}>
              {errorMsg}
            </Alert>
          </Snackbar>
          <Dialog open={openMapDialog} fullWidth maxWidth="sm">
            <DialogTitle>
              <MDBox>Current Location</MDBox>
            </DialogTitle>
            <DialogContent>
              <MDBox mt={2} mb={3}>
                {coords && (
                  <div style={{ height: "100vh", width: "100%" }}>
                    <GoogleMapReact
                      bootstrapURLKeys={{
                        key: "AIzaSyCmiv_wy1kjzBT6tcvMK56pdhfpG32Ljw0",
                      }}
                      defaultCenter={{
                        lat: coords.latitude,
                        lng: coords.longitude,
                      }}
                      defaultZoom={14}
                    >
                      <Icon
                        fontSize="large"
                        color="error"
                        lat={coords.latitude}
                        lng={coords.longitude}
                        text="Current Location"
                      >
                        pin_drop
                      </Icon>
                    </GoogleMapReact>
                  </div>
                )}
              </MDBox>
            </DialogContent>
            <DialogActions>
              <MDButton
                onClick={() => {
                  setOpenMapDialog(false);
                }}
                color="dark"
                iconOnly
                circular
                variant="outlined"
              >
                <Icon>close</Icon>
              </MDButton>
              &nbsp;
              <MDButton
                color="info"
                variant="outlined"
                iconOnly
                circular
                onClick={() => {
                  setOpenMapDialog(false);
                  captureLocation();
                }}
              >
                <Icon>save</Icon>
              </MDButton>
            </DialogActions>
          </Dialog>
          <MDBox>
            <Grid container spacing={3}>
              <Grid item xs={8} md={8} lg={8}>
                <MDTypography
                  variant="h6"
                  fontWeight="light"
                  fontSize="small"
                  mb={2}
                >
                  {displayName}
                </MDTypography>
              </Grid>
              <Grid item xs={4} md={4} lg={4}>
                <MDBox align="right">
                  <MDButton
                    iconOnly
                    size="small"
                    variant="contained"
                    color="primary"
                    // startIcon={<Icon>location_on</Icon>}
                    onClick={() => {
                      const gpsCoords = getGPSCoords();
                      if (gpsCoords === false) {
                        console.log(options);
                        setErrorMsg(
                          "Unable to retrieve your current location. Make sure Location permissions are allowed on your browser.",
                        );
                        showFieldError(true);
                        return;
                      }
                      setGpsLocationCoords(null);
                      setOpenMapDialog(true);
                    }}
                  >
                    <Icon>gps_fixed</Icon>
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
          <MDBox>
            {gpsLocationCoords !== null && coords && (
              <MDTypography variant="caption" fontSize={14} fontWeight="light">
                <b>
                  <Icon color="error">pin_drop</Icon>&nbsp;GPS Captured
                </b>
                <br />
                <small>
                  Latitude: {coords.latitude} | Longitude: {coords.longitude}
                </small>
              </MDTypography>
            )}
          </MDBox>
          <input
            type="hidden"
            id={`dff_${fieldRef}`}
            name={`${fieldRef}`}
            value={gpsLocationCoords}
          />
        </MDBox>
      )}
    </MDBox>
  );
}

// Typechecking props of the Transaction
CurrentGPSLocation.propTypes = {
  formRef: PropTypes.string.isRequired,
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.any),
};

CurrentGPSLocation.defaultProps = {
  options: null,
};

export default CurrentGPSLocation;
